import { baseApi } from "./baseApi";

const JSON_HEADERS = {
  "Content-type": "application/json; charset=UTF-8",
};

const mergePagedUsers = (currentCache, incoming, { arg }) => {
  if (!currentCache || arg?.page === 1) {
    return incoming;
  }

  const incomingData = incoming?.data ?? {};
  const existingUsers = Array.isArray(currentCache.data?.users)
    ? currentCache.data.users
    : [];
  const incomingUsers = Array.isArray(incomingData.users)
    ? incomingData.users
    : [];
  const seen = new Set(existingUsers.map((user) => user.slug).filter(Boolean));

  currentCache.data = {
    ...(currentCache.data || {}),
    ...incomingData,
    users: [
      ...existingUsers,
      ...incomingUsers.filter((user) => !user.slug || !seen.has(user.slug)),
    ],
  };

  // Always keep the latest pagination links from the API envelope
  if (incoming?.links) {
    currentCache.links = incoming.links;
  }
  if (incoming?.message) currentCache.message = incoming.message;
  if (incoming?.code != null) currentCache.code = incoming.code;
};

export const engagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEngagementSegments: builder.query({
      query: () => ({
        url: "engagement/segments",
        method: "GET",
        headers: JSON_HEADERS,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["EngagementSegments"],
    }),
    getSubscriptionUsers: builder.query({
      query: ({ filter, page = 1, limit = 20 }) => ({
        url: `engagement/users/subscription?filter=${encodeURIComponent(
          filter
        )}&page=${page}&limit=${limit}`,
        method: "GET",
        headers: JSON_HEADERS,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.filter}`,
      merge: mergePagedUsers,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.filter !== previousArg?.filter,
      keepUnusedDataFor: 0,
    }),
    getActivityUsers: builder.query({
      query: ({ filter, page = 1, limit = 20 }) => ({
        url: `engagement/users/activity?filter=${encodeURIComponent(
          filter
        )}&page=${page}&limit=${limit}`,
        method: "GET",
        headers: JSON_HEADERS,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.filter}`,
      merge: mergePagedUsers,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.filter !== previousArg?.filter,
      keepUnusedDataFor: 0,
    }),
    getEngagementCampaigns: builder.query({
      query: () => ({
        url: "engagement/campaigns",
        method: "GET",
        headers: JSON_HEADERS,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["EngagementCampaigns"],
    }),
    getEngagementOutcomes: builder.query({
      query: () => ({
        url: "engagement/outcomes",
        method: "GET",
        headers: JSON_HEADERS,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["EngagementOutcomes"],
    }),
    getCampaignUsers: builder.query({
      query: ({ slug, page = 1, limit = 20 }) => ({
        url: `engagement/campaigns/${encodeURIComponent(
          slug
        )}/users?page=${page}&limit=${limit}`,
        method: "GET",
        headers: JSON_HEADERS,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.slug}`,
      merge: mergePagedUsers,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.slug !== previousArg?.slug,
      keepUnusedDataFor: 0,
    }),
    runEngagementCampaigns: builder.mutation({
      query: () => ({
        url: "engagement/run",
        method: "POST",
        headers: JSON_HEADERS,
      }),
      invalidatesTags: [
        "EngagementSegments",
        "EngagementCampaigns",
        "EngagementOutcomes",
      ],
    }),
  }),
});

export const {
  useGetEngagementSegmentsQuery,
  useGetSubscriptionUsersQuery,
  useLazyGetSubscriptionUsersQuery,
  useGetActivityUsersQuery,
  useLazyGetActivityUsersQuery,
  useGetEngagementCampaignsQuery,
  useGetEngagementOutcomesQuery,
  useGetCampaignUsersQuery,
  useLazyGetCampaignUsersQuery,
  useRunEngagementCampaignsMutation,
} = engagementApi;
